import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { LazyImage } from "./LazyImage";

// Two premium vertical floating cards — Women / Men — with a soft champagne-gold
// stroke and a subtle CSS zoom-in on hover. Stacks vertically on mobile.
const cards = [
  {
    key: "her",
    eyebrow: "For Her · The Edit",
    title: "Effortlessly elegant.",
    blurb: "Hand-finished sarees, lehengas & co-ords.",
    cta: "Shop Women",
    img: "https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=900&q=85&auto=format&fit=crop",
    accent: "#FFD1D1",
    glow: "rgba(255,209,209,0.4)",
  },
  {
    key: "him",
    eyebrow: "For Him · The Drop",
    title: "Streetwear, refined.",
    blurb: "Modern silhouettes meet Indian craft.",
    cta: "Shop Men",
    img: "https://images.unsplash.com/photo-1622519407650-3df9883f76a5?w=900&q=85&auto=format&fit=crop",
    accent: "#D1E8FF",
    glow: "rgba(209,232,255,0.4)",
  },
];

const goToStorefront = () => {
  document
    .getElementById("storefront")
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const DualModelBanner = () => {
  return (
    <section
      data-testid="dual-model-banner"
      className="w-full"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        {cards.map((c, idx) => (
          <motion.article
            key={c.key}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`model-card-${c.key}`}
            className="group relative rounded-3xl overflow-hidden cursor-pointer aspect-[3/4] sm:aspect-[4/5] border-2 border-[#E0B53C]/30 hover:border-[#E0B53C]/75 bg-white/40 backdrop-blur-xl shadow-[0_12px_36px_rgba(2,44,34,0.12)] hover:shadow-[0_22px_56px_rgba(224,181,60,0.22)] transition-all duration-500"
            onClick={goToStorefront}
          >
            {/* Tonal wash behind the image for depth on slow load */}
            <div
              className="absolute inset-0"
              style={{ backgroundColor: c.accent, opacity: 0.5 }}
              aria-hidden="true"
            />

            {/* Hero image with smooth zoom-in on hover */}
            <LazyImage
              src={c.img}
              alt={c.title}
              wrapperClassName="absolute inset-0 w-full h-full"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.03] mix-blend-multiply"
            />

            {/* Bottom-up gradient for legibility */}
            <div
              className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent pointer-events-none"
              aria-hidden="true"
            />

            {/* Top-right pill */}
            <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur-md text-[10px] uppercase tracking-[0.22em] text-[#E0B53C] font-semibold px-3 py-1.5 border border-[#E0B53C]/30">
              <span
                className="w-1 h-1 rounded-full bg-[#E0B53C]"
                style={{ boxShadow: `0 0 8px ${c.glow}` }}
              />
              New drop
            </div>

            {/* Copy block */}
            <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 text-white">
              <p
                className="text-[11px] uppercase tracking-[0.24em] font-semibold mb-1.5"
                style={{ color: "#E0B53C" }}
                data-testid={`model-card-eyebrow-${c.key}`}
              >
                {c.eyebrow}
              </p>
              <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight tracking-tight font-light">
                {c.title}
              </h3>
              <p className="text-sm text-white/80 mt-2 max-w-[90%]">{c.blurb}</p>
              <span
                className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium border-b border-[#E0B53C]/60 hover:border-[#E0B53C] pb-1 transition-all group-hover:gap-2.5"
                data-testid={`model-card-cta-${c.key}`}
                style={{ color: "#E0B53C" }}
              >
                {c.cta}
                <ArrowUpRight size={14} strokeWidth={2} />
              </span>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
};
